<template>
  <section :class="[postContextClass, {focus: focusInput}, {stories: $isEngCom}, {'isEng' : $isStories}]">
      <div v-if="!focusInput && !update" class="placeholder-container is-flex cursor-pointer print-no" tabindex="0" @keypress.enter.space="focus('text')" key="placeholder" @click="focus('text')">
        <div class="placeholder is-flex" qa="new_comment_field">
          <span class="is-hidden-mobile">{{contributionType}}</span>
          <span class="is-hidden-tablet">{{ $t("projectPage.reply") }}</span>
        </div>
        <buttons :hideTextOption="true" :context="context" @click="focus"></buttons>
      </div>
      <div class="comment-editor" @paste="onPaste" v-if="focusInput || update">
        <preloader v-if="saving && uploadingAttachments" :attachmentsLoaded='uploadedAttachments' :attachmentsLength='attachments.length' mode='animate' :background="true"></preloader>
        <attachments ref="attachments" v-model="attachments" v-bind="{context, saving, post, comment, carouselPosition}" @edited="edited = true"></attachments>
        <post-body id="post-body" ref="postBody" class="post-body" v-bind="{placeholder}" v-model="body" @input="onChange"></post-body>
        <div v-if="error" class="error help has-text-danger"> * {{error}}</div>

        <div class="button-container is-flex" :class="{saving: saving}">
          <div v-if="!saving && update" @click="$emit('delete')" class="button del is-outlined is-small has-text-grey"><i class="fal fa-trash" aria-hidden="true"></i></div>
          <div class="cancel-and-save">
            <button v-if="!saving" tabindex="0" @click="handleCancel" class="button cancel is-small is-outlined is-primary is-uppercase"> {{ $t("cancel") }} </button>
            <button class="save button is-primary is-small is-uppercase gtm-add-comment" tabindex="0" @click="save" :disabled="saving || !canSave">
              <span v-if="!saving" qa="post_comment_btn">{{label}}</span>
              <span v-if="saving && uploadingAttachments" class="saving-text">{{ $t("projectPage.savingAttachment2") }} {{uploadedAttachments + 1}} {{ $t("projectPage.of") }} {{attachments.length}}</span>
              <span v-if="saving && !uploadingAttachments" class="saving-text">{{ $t("projectPage.saving") }}</span>
              <progress  v-if="saving && uploadingAttachments" class="progress is-primary saving-text" :value="monitor.progress" max="100"></progress>
            </button>
          </div>
        </div>
      </div>
      <div v-if="!update" class="avatar-container">
        <img class="avatar" :src="avatar" alt="">
      </div>
  </section>
</template>
<script>
import imageProxy from '@/libs/image-proxy'

import PostBody from '@/components/Inputs/QuillEditor'
import Buttons from '../Attachments/Buttons'
import Preloader from '@/components/Controls/Preloader'
import base from '../mixins/base'
import save from '../mixins/save'
import i18n from '@/i18n'

const dispatch = {
  create: 'createComment',
  update: 'updateComment',
  get: 'getComment',
  addAttachments: 'addCommentAttachments',
  updateAttachments: 'updateCommentAttachment',
  deleteAttachments: 'deleteCommentAttachment'
}

export default {
  props: ['type', 'project', 'selectedProjectID', 'isComment', 'post', 'comment', 'update', 'context', 'carouselPosition', 'replyTo', 'isGrid'],

  components: {
    PostBody,
    Buttons,
    Preloader
  },

  mixins: [base, save],

  data: function () {
    return {
      dispatch,
      commentLength: 0,
      focusInput: false,
      isMounted: false,
      hasNonCharContent: false,
      hasProfanity: false,
      error: ''
    }
  },

  computed: {
    entity: function () {
      return this.comment || null
    },
    label: function () {
      if (this.update) {
        return i18n.t('projectPage.save')
      } else {
        return this.replyTo.contribution !== null ? `${i18n.t('projectPage.replyTo')} ${this.replyTo.contribution.user.userName}` : i18n.t('projectPage.post')
      }
    },
    placeholder: function () {
      return !this.attachments.length ? i18n.t('projectPage.commentPlaceholder') : i18n.t('projectPage.addCaption')
    },
    canSave: function () {
      return (this.attachments.length > 0 || this.commentLength || this.hasNonCharContent || !this.hasProfanity) && (this.projectID || this.isComment)
    },
    projectID: function () {
      if (this.project) {
        return this.project.id
      }
      if (this.post) {
        return this.post.projectID
      }
      return this.selectedProjectID || 0
    },
    createKey: function () {
      return this._uicreateKey(this.projectID, this.comment, this.post, this.update)
    },
    annotateKey: function () {
      return this.post ? `commentFrom.${this.post.id}` : null
    },
    avatar: function () {
      return this.isLoggedIn ? imageProxy.thumbnail(this.currentUser.avatar) : '/static/leo.svg'
    },
    contributionType: function () {
      return this.$isEngCom ? 'Share your thoughts' : i18n.t('projectPage.replyToThisSection')
    }
  },

  watch: {
    'replyTo.contribution': function (userObj) {
      this.addAtReply(userObj)
    },
    parentID: function () {
      return this.replyTo.saveParentID ? this.replyTo.contribution.id : null
    }
  },

  mounted: function () {
    if (this.$refs.postBody) {
      this.$refs.postBody.setEncodedContents(this.body)
      this.$nextTick(() => {
        this.$store.dispatch('setEdited', { id: this.post.id, edited: false })
      })
    }
    this.isMounted = true
  },

  methods: {
    onChange: function (text) {
      if (!this.isMounted) {
        return
      }
      this.$store.dispatch('setEdited', { id: this.post.id, edited: true })
      this.commentLength = this.$refs.postBody.quillText ? this.$refs.postBody.quillText.trim().length : 0
      this.hasNonCharContent = this.$refs.postBody?.hasNonCharContent
      this.hasProfanity = this.$refs.postBody?.hasProfanity()
    },
    focus: function (type) {
      this.focusInput = true
      if (!this.isMobile) {
        this.$nextTick(() => {
          this.$refs.postBody.focusAtEnd()
        })
      }

      if (type && type !== 'text') {
        this.$nextTick(() => { this.$refs.attachments.addAttachment(type) })
      }
    },
    addAtReply: function (userObj) {
      this.$nextTick(() => {
        if (this.$refs?.postBody?.getUserToReply) {
          this.$refs.postBody.getUserToReply(userObj.user)
        }
      })
    },
    createParams: function () {
      return {
        postID: this.post.id,
        comment: this.body,
        parentID: this.parentID,
        attachments: this.attachments
      }
    },
    updateParams: function () {
      return {
        id: this.comment.id,
        body: this.body,
        attachments: this.attachments
      }
    },
    socketParams: function () {
      return ['project', this.post.projectID, 'comment', this.parentID, 'post', this.post.id]
    },
    scrollIntoView: function () {
      const attachments = this.$refs.attachments
      if (attachments) {
        const rect = attachments.$el.getBoundingClientRect()
        window.scrollTo({
          top: (window.scrollY || window.pageYOffset) + rect.top - 440, // account for nav bar
          behavior: 'smooth'
        })
      }
    },
    handleCancel: function () {
      this.body = ''
      this.$emit('cancel')
      this.focusInput = false
      this.saved = true // prevents updateKey from setting on beforeDestroy
      this.error = ''
      this.attachments = []
      this.isCancelling = true
    },
    getStoreData: function () {
      return {
        projectID: this.projectID,
        commentID: (this.comment ? this.comment.id : null),
        body: this.body,
        attachments: this.attachments,
        uploadedAttachments: this.uploadedAttachments
      }
    },
    checkCanSave: function () {
      return this.canSave
    },
    onPaste (evt) {
      if (evt.clipboardData.files.length > 0) {
        evt.preventDefault()
        var err = i18n.t('comments.pastedImageError')
        return err
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  section{
    position: relative;
  }
  .button-container {
    justify-content: space-between;
    margin: 0.5rem 0 0;
    align-items: center;
    &.saving {
      justify-content: flex-end;
    }
    .save {
      border: none;
      margin-left: 1rem;
      color: $white;
      margin-left: 0.25rem;
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
  }

  .create-comment {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    border-radius: 15px;
    margin-top: 1rem;
    margin-left: 2.45rem;
    background-color: $white;
    box-shadow: 0px 3px 10px -1px rgba(138,138,138,0.3);
    @include mobile {
      margin-left: 1.6rem;
      margin-right: 0.35rem;
      padding: 0;
    }
    .avatar-container {
      width: 2.85rem;
      min-width: 2.85rem;
      height: 2.85rem;
      max-width: none;
      user-select: none;
      position: absolute;
      z-index: 3;
      left: -3.5rem;
      top: 0.1rem;
      margin-left: 1.1rem;
      margin-right: 0.5rem;
      @include mobile {
        width: 2.2rem;
        min-width: 2.2rem;
        height: 2.2rem;
        top: 0.4rem;
        margin-left: 2.1rem;
      }
      .avatar {
        border-radius: 50%;
        height: 100%;
        width: 100%;
        max-width: 100%;
        max-height: 100%;
      }
    }
  }
  .create-comment {
    .button-container {
      justify-content: flex-end;
    }
  }

  .stories {
    border-top: 0rem;
  }

.button {
  span {
    z-index: 2;
  }
  .progress {
    z-index: 1;
    opacity: 0.3;
    height: 100%;
    &::-webkit-progress-bar {
      background-color: white;
    }
  }
  .saving-text {
    font-size: 0.9rem;
  }
}

.posting-error {
  width: 100%;
  justify-content: flex-end;
  font-weight: 700;
  @include mobile {
    flex-direction: column;
    font-size: 0.9rem;
    align-items: flex-end;
  }
}

.placeholder-container {
  width: 100%;
  position: relative;
  background-color: white;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.5rem 0 1.25rem;
  border-radius: 10px;
  .placeholder {
    min-height: 29px;
    resize: none;
    color: $grey;
    align-items: center;
    height: 100%;
  }
}

.comment-editor {
  width: 100%;
  height: 100%;
  padding: 0.5rem;
  position: relative;
  overflow: visible;
  padding-right: 1.5rem;
}

.create-comment {
  .comment-editor {
    margin: 0.5rem;
  }
}

.create-comment-update {
  margin: 0.5rem;
}

.isEng{
  background-color: #F8F8F8;
}

.cancel-and-save {
  div {
    margin-left: 1rem;
    border-radius: 1px;
    font-size: .65rem;
    min-width: 72px;
    height: 28px;
  }
}

</style>
